import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Button } from "@/components";
import { useScroll, useMobile } from "@/hooks";
import emitter, { Events } from "@/event";
import { trackMenuClick, trackContactClick } from "@/lib/analytics";
import logo from "@/assets/images/logo.png";
import "./style.scss";
import "./style.mobile.scss";

interface LinkType {
  path: string;
  label: string;
  event?: keyof Events;
}

const MOBILE_LINKS: LinkType[] = [
  {
    path: "/ai-shopping-agent",
    label: "AI Agents",
    event: "SCROLL_TO_AI_SHOPPING_AGENT",
  },
  {
    path: "/e-commerce-marketplace",
    label: "E-commerce Marketplace",
    event: "SCROLL_TO_E_COMMERCE_MARKETPLACE",
  },
  {
    path: "/influencer-publisher",
    label: "Influencer And Publisher",
    event: "SCROLL_TO_INFLUENCER_PUBLISHER",
  },
  {
    path: "/drop-shipping-merchant",
    label: "Drop-shipping Merchant",
    event: "SCROLL_TO_DROP_SHIPPING_MERCHANT",
  },
  {
    path: "/geo",
    label: "GEO",
    event: "SCROLL_TO_GEO",
  },
  {
    path: "/shopgeni",
    label: "ShopGeni",
    event: "SCROLL_TO_SHOPGENI",
  },
  {
    path: "/price-hunter",
    label: "Price Hunter",
    event: "SCROLL_TO_PRICE_HUNTER",
  },
  {
    path: "/merchants-brands",
    label: "Brands",
    event: "SCROLL_TO_MERCHANT",
  },
  // {
  //   path: "/brand-launch",
  //   label: "BrandsLaunch",
  //   event: "SCROLL_TO_BRAND_LAUNCH",
  // },
  {
    path: "/company",
    label: "Company",
  },
  {
    path: "/articles/",
    label: "Articles",
  },
];

type Theme = "dark" | "light" | "hoary";

const Header: FC = () => {
  const headerRef = useRef<HTMLHeadElement>(null);
  const [fixed, setFixed] = useState(false);
  const [show, setShow] = useState(false);
  const location = useLocation();
  const [isMobile] = useMobile();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [visible, setVisible] = useState({
    solution: false,
    brands: false,
  });

  useScroll((e) => {
    if (e.scrollTop > 0) {
      setFixed(true);
      if (e.direction === "top") {
        setShow(true);
      } else {
        setShow(false);
        setVisibleHandler({ solution: false, brands: false });
      }
    } else {
      setFixed(false);
    }
  });

  const getTheme = (): Theme => {
    switch (location.pathname) {
      case "/":
      case "/e-commerce-marketplace":
      case "/influencer-publisher":
      case "/drop-shipping-merchant":
      case "/merchants-brands":
        return "hoary";
      case "/company":
        return "dark";
      default:
        return "light";
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile || !open) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [isMobile, open]);

  const theme = getTheme();

  const onNavigate = (link: LinkType) => {
    navigate(link.path);
    setOpen(false);
  };

  const setVisibleHandler = (data: Partial<typeof visible>) => {
    setVisible((pre) => ({ ...pre, ...data }));
  };

  const backHome = () => {
    trackMenuClick("Logo");
    window.scroll(0, 0);
  };

  return (
    <>
      <header ref={headerRef} className={classNames("page-header", theme)}>
        <div className={classNames("header-content", { fixed, show })}>
          <a href="/#/" onClick={backHome}>
            <h1 className="title">
              <img src={logo} alt="CloudStore AI" className="logo" />
              CloudStore AI
            </h1>
          </a>
          <ul className="link-group">
            {!isMobile && (
              <>
                <li
                  className={classNames("link-item anchor")}
                  onMouseEnter={() => setVisibleHandler({ solution: true })}
                  onMouseLeave={() => setVisibleHandler({ solution: false })}
                >
                  <span className="link-item-text">Solution</span>
                  <div
                    className={classNames("anchor-group", {
                      show: visible.solution,
                    })}
                  >
                    {MOBILE_LINKS.slice(0, 4).map((item) => (
                      <Link
                        onClick={() => {
                          trackMenuClick(item.label);
                          setVisibleHandler({ solution: false });
                          item.event && emitter.emit(item.event);
                        }}
                        to={item.path}
                        key={item.path}
                      >
                        <div className="link-inner">
                          <span>{item.label}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </li>

                <li
                  className={classNames("link-item", "brands")}
                  onMouseEnter={() => setVisibleHandler({ brands: true })}
                  onMouseLeave={() => setVisibleHandler({ brands: false })}
                >
                  <Link
                    to="/merchants-brands"
                    onClick={() => {
                      trackMenuClick("Brands");
                      emitter.emit("SCROLL_TO_MERCHANT");
                    }}
                  >
                    <span className="link-item-text">Brands</span>
                  </Link>
                  <div
                    className={classNames("anchor-group", {
                      show: visible.brands,
                    })}
                  >
                    {/* <Link
                      onClick={(e) => {
                        setVisibleHandler({ brands: false });
                        emitter.emit("SCROLL_TO_BRAND_LAUNCH");
                      }}
                      to="/brand-launch"
                    >
                      <div className="link-inner">
                        <span>BrandLaunch</span>
                      </div>
                    </Link> */}
                    {MOBILE_LINKS.slice(4, 7).map((item) => (
                      <Link
                        onClick={() => {
                          trackMenuClick(item.label);
                          setVisibleHandler({ brands: false });
                          item.event && emitter.emit(item.event);
                        }}
                        to={item.path}
                        key={item.path}
                      >
                        <div className="link-inner">
                          <span>{item.label}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </li>
                <li className="link-item">
                  <Link to="/company" onClick={() => trackMenuClick("Company")}>
                    <span
                      className={classNames("link-item-text", {
                        active: location.pathname === "/company",
                      })}
                    >
                      Company
                    </span>
                  </Link>
                </li>
                <li className="link-item">
                  {/* Articles live on real paths (/articles), outside the hash
                      router, so use a real anchor for a full page navigation. */}
                  <a href="/articles/" onClick={() => trackMenuClick("Articles")}>
                    <span className="link-item-text">Articles</span>
                  </a>
                </li>
              </>
            )}
            <li className="link-item">
              <Link
                className="contact-us"
                to="/contact-us"
                onClick={() => trackContactClick("Header")}
              >
                Contact Us
              </Link>
            </li>
            {isMobile && (
              <li className="link-item">
                <div className="open-icon" onClick={() => setOpen(true)}>
                  <span className="icon-line line-1"></span>
                  <span className="icon-line line-2"></span>
                  <span className="icon-line line-3"></span>
                </div>
              </li>
            )}
          </ul>
        </div>
      </header>
      {isMobile &&
        createPortal(
          <>
            <div className={classNames("aside-mask", { open })}></div>
            <div className={classNames("aside-content", { open })}>
              <div className="open-icon" onClick={() => setOpen(false)}>
                <span className="icon-line line-1"></span>
                <span className="icon-line line-3"></span>
              </div>
              <div className="aside-scroll">
                <ul>
                  {MOBILE_LINKS.map((item) =>
                    item.path.startsWith("/articles") ? (
                      // Real-path section outside the hash router: full page load.
                      <li className="link-item" key={item.path}>
                        <a
                          href={item.path}
                          onClick={() => {
                            trackMenuClick(item.label);
                            setOpen(false);
                          }}
                        >
                          {item.label}
                        </a>
                      </li>
                    ) : (
                      <li className="link-item" key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => {
                            trackMenuClick(item.label);
                            setOpen(false);
                            item.event && emitter.emit(item.event);
                          }}
                        >
                          {item.label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </div>
              <div className="aside-footer">
                <Button
                  onClick={() => {
                    trackContactClick("Header");
                    onNavigate({
                      path: "/contact-us",
                      label: "",
                    });
                  }}
                  icon={false}
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default Header;
